import type { FlowSchedule } from "@cohesive/api-client";
import {
  generateCronExpression,
  parseCronExpression,
  type ScheduleType,
} from "@cohesive/ui-components";
import { isStructuredCron } from "./cadence";

/** "none" = no schedule (created paused / fully off). */
export type ScheduleDraftType = ScheduleType | "none";

/** The structured editor's model — everything needed to emit a cron + zone. */
export interface ScheduleDraft {
  scheduleType: ScheduleDraftType;
  /** "HH:mm" */
  time24: string;
  /** Cron day numbers, "0" (Sun) – "6" (Sat). */
  daysOfWeek: string[];
  /** "1" – "31". */
  daysOfMonth: string[];
  /** "1" – "12" (annual only). */
  month: string;
  /** IANA zone; always sent explicitly so the server default never applies. */
  timeZone: string;
}

export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

export function supportedTimeZones(): string[] {
  const intl = Intl as typeof Intl & {
    supportedValuesOf?: (key: "timeZone") => string[];
  };
  try {
    return intl.supportedValuesOf?.("timeZone") ?? [browserTimeZone()];
  } catch {
    return [browserTimeZone()];
  }
}

/**
 * False when the schedule's cadence can't be represented by the structured
 * controls — a custom cron, or a one-off. Those render read-only until the
 * user explicitly replaces them, so an unrepresentable cadence is never
 * silently rewritten.
 */
export function usesStructuredEditor(
  schedule: Pick<FlowSchedule, "cron" | "next_run_at">,
): boolean {
  if (schedule.cron) return isStructuredCron(schedule.cron);
  // No cron at all: unscheduled (editable) unless it's a pending one-off.
  return !schedule.next_run_at;
}

export function defaultScheduleDraft(
  overrides?: Partial<ScheduleDraft>,
): ScheduleDraft {
  return {
    scheduleType: "daily",
    time24: "09:00",
    daysOfWeek: ["1"],
    daysOfMonth: ["1"],
    month: "1",
    timeZone: browserTimeZone(),
    ...overrides,
  };
}

/**
 * Seed the editor from a schedule. Only call when the cron is structured (or
 * absent) — custom crons render the read-only fallback instead.
 */
export function draftFromSchedule(
  schedule: Pick<FlowSchedule, "cron" | "cron_time_zone">,
): ScheduleDraft {
  const timeZone = schedule.cron_time_zone || browserTimeZone();
  if (!schedule.cron || !isStructuredCron(schedule.cron)) {
    return defaultScheduleDraft({ scheduleType: "none", timeZone });
  }
  const parsed = parseCronExpression(schedule.cron);
  return defaultScheduleDraft({
    scheduleType: parsed.scheduleType,
    time24: parsed.time24 ?? "09:00",
    daysOfWeek: parsed.daysOfWeek?.length ? parsed.daysOfWeek : ["1"],
    daysOfMonth: parsed.daysOfMonth?.length ? parsed.daysOfMonth : ["1"],
    month: parsed.month ?? "1",
    timeZone,
  });
}

/**
 * The stored cadence in the form the structured controls emit, so a saved
 * schedule can be compared against a draft without cosmetic differences
 * reading as an edit: `parseCronExpression` pads the hour and expands weekday
 * ranges, so a server-side `0 9 * * 1-5` regenerates as `0 09 * * 1,2,3,4,5`.
 * Unrepresentable cadences keep their raw cron — replacing one is a real edit.
 */
export function savedCronForCompare(
  schedule: Pick<FlowSchedule, "cron" | "cron_time_zone" | "next_run_at">,
): string {
  if (!usesStructuredEditor(schedule)) return schedule.cron ?? "";
  return draftToCron(draftFromSchedule(schedule)) ?? "";
}

/** null = no schedule. */
export function draftToCron(draft: ScheduleDraft): string | null {
  if (draft.scheduleType === "none") return null;
  return generateCronExpression({
    scheduleType: draft.scheduleType,
    timeDisplay: "",
    time24: draft.time24,
    daysOfWeek: draft.daysOfWeek,
    daysOfMonth: draft.daysOfMonth,
    month: draft.month,
  });
}
